import { lazy, Suspense, startTransition, useCallback, useEffect, useMemo, useState, ViewTransition, type ReactNode } from "react";
import { api, onAuthEvent } from "./api.js";
import { Login } from "./pages/Login.js";
import { useI18n } from "./useI18n.js";
import { clearMe, refreshMe, type Me } from "./useMe.js";
import { parseRoute, routeTitleKey, type Route } from "./routes.js";
import { ShellContext, requestHeadingFocus, type Shell } from "./ui/shell.js";
import { ErrorBoundary } from "./ui/ErrorBoundary.js";
import { PageSkeleton } from "./ui/Page.js";
import { ToastRegion } from "./ui/Toast.js";
import { EmptyState, PageHeader } from "./ui/Page.js";
import { Sidebar, TabBar, MenuSheet } from "./shell/Nav.js";
import { AsOfBand, OfflineBanner, RecoveryNoticeBanner, UpdateBanner, type RecoveryNotice } from "./shell/Banners.js";
import { SearchPalette } from "./shell/Search.js";
import { notifications } from "./notifStore.js";
import { flushDrafts, clearAllDrafts, setDraftUser } from "./drafts.js";
import { setTelemetrySignedIn } from "./telemetry.js";
import { viewpoint } from "./viewpoint.js";

// 画面ごとにコード分割する（初回の読み込みを軽くする）。読み込みに失敗したら ErrorBoundary が
// 「新しい版があります」を出す（デプロイで古いファイルが消えた場合）。
// 画面のモジュールを読む関数（同じ Promise を使い回す。下の preloadRoute が先回りして読む）
const load = {
  Home: once(() => import("./pages/Home.js")),
  Notifications: once(() => import("./pages/Notifications.js")),
  Dashboard: once(() => import("./pages/Dashboard.js")),
  Obligations: once(() => import("./pages/Obligations.js")),
  Incidents: once(() => import("./pages/Incidents.js")),
  Report: once(() => import("./pages/Report.js")),
  Training: once(() => import("./pages/Training.js")),
  Criteria: once(() => import("./pages/Criteria.js")),
  Settings: once(() => import("./pages/Settings.js")),
  Roles: once(() => import("./pages/Roles.js")),
  Governance: once(() => import("./pages/Governance.js")),
  Coverage: once(() => import("./pages/Coverage.js")),
  RegisterList: once(() => import("./pages/RegisterList.js")),
  RegisterEntries: once(() => import("./pages/RegisterEntries.js")),
  Requests: once(() => import("./pages/Requests.js")),
  RequestNew: once(() => import("./pages/RequestNew.js")),
  RequestDetail: once(() => import("./pages/RequestDetail.js")),
};
const Home = lazy(() => load.Home().then((m) => ({ default: m.Home })));
const NotificationsPage = lazy(() => load.Notifications().then((m) => ({ default: m.NotificationsPage })));
const Dashboard = lazy(() => load.Dashboard().then((m) => ({ default: m.Dashboard })));
const Obligations = lazy(() => load.Obligations().then((m) => ({ default: m.Obligations })));
const Incidents = lazy(() => load.Incidents().then((m) => ({ default: m.Incidents })));
const ReportPage = lazy(() => load.Report().then((m) => ({ default: m.ReportPage })));
const Training = lazy(() => load.Training().then((m) => ({ default: m.Training })));
const Criteria = lazy(() => load.Criteria().then((m) => ({ default: m.Criteria })));
const Settings = lazy(() => load.Settings().then((m) => ({ default: m.Settings })));
const TotpOnlySetup = lazy(() => load.Settings().then((m) => ({ default: m.TotpOnlySetup })));
const Roles = lazy(() => load.Roles().then((m) => ({ default: m.Roles })));
const Governance = lazy(() => load.Governance().then((m) => ({ default: m.Governance })));
const Coverage = lazy(() => load.Coverage().then((m) => ({ default: m.Coverage })));
const RegisterList = lazy(() => load.RegisterList().then((m) => ({ default: m.RegisterList })));
const RegisterEntries = lazy(() => load.RegisterEntries().then((m) => ({ default: m.RegisterEntries })));
const RequestList = lazy(() => load.Requests().then((m) => ({ default: m.RequestList })));
const RequestNew = lazy(() => load.RequestNew().then((m) => ({ default: m.RequestNew })));
const RequestDetailView = lazy(() => load.RequestDetail().then((m) => ({ default: m.RequestDetailView })));

/** 今の URL の画面を、ログインの確認（/auth/me）を待たずに読み始める（読み込みの段を1つ減らす）。 */
function preloadRoute(r: Route): void {
  const m: Partial<Record<Route["name"], () => Promise<unknown>>> = {
    home: load.Home, notifications: load.Notifications, readiness: load.Dashboard, obligations: load.Obligations,
    incidents: load.Incidents, report: load.Report, training: load.Training, criteria: load.Criteria, settings: load.Settings,
    roles: load.Roles, governance: load.Governance, coverage: load.Coverage, list: load.RegisterList, register: load.RegisterEntries,
    requests: load.Requests, requestNew: load.RequestNew, request: load.RequestDetail,
  };
  void m[r.name]?.().catch(() => { /* 失敗はエラー境界が拾う */ });
}

function once<T>(f: () => Promise<T>): () => Promise<T> {
  let p: Promise<T> | null = null;
  // 失敗したら次に呼ばれたときに読み直す（デプロイ直後の一時的な失敗で固まらないように）
  return () => (p ??= f().catch((e) => { p = null; throw e; }));
}

preloadRoute(parseRoute(location.hash));

function routeKey(r: Route): string {
  return r.name === "register" ? `register:${r.code}` : r.name === "request" ? `request:${r.id}` : r.name;
}

export function App() {
  const { t } = useI18n();
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);
  const [route, setRoute] = useState<Route>(() => parseRoute(location.hash));
  // H-02: ログイン画面に戻ったときだけ出す「ログインが切れました」の案内。
  const [sessionNotice, setSessionNotice] = useState(false);
  // review-4 M2-c: 復旧の操作の後の最初のログインで一度だけ出す（サーバは返した時点で消している。メモリにだけ持つ）
  const [recoveryNotice, setRecoveryNotice] = useState<RecoveryNotice | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [past, setPast] = useState(false);

  const refresh = useCallback(() => {
    api.get<{ user: Me }>("/auth/me")
      // M3: 下書きの持ち主を画面を出す前に知らせる（別の利用者の下書きはここで消える）
      .then((r) => { setDraftUser(r.user.userId); setMe(r.user); })
      .catch(() => setMe(null))
      .finally(() => setChecked(true));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // ハッシュの変化（戻る・進む・リンク）を画面の切替にする。View Transition に乗せるため transition で。
  useEffect(() => {
    const onHash = () => {
      requestHeadingFocus();
      startTransition(() => setRoute(parseRoute(location.hash)));
      window.scrollTo(0, 0);
    };
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  // ブラウザのタブに画面名を出す
  useEffect(() => {
    const key = routeTitleKey(route);
    document.title = key === "app.title" ? t("app.title") : `${t(key)} - ${t("app.title")}`;
  }, [route, t]);

  // ログイン中だけ: 通知の取得とクライアントのエラー送信
  useEffect(() => {
    setTelemetrySignedIn(!!me);
    if (me) notifications.start(); else notifications.stop();
  }, [me]);

  // H-02: どの画面のどの呼び出しが 401 を受けても、同じ場所で同じ扱いにする。
  // 画面ごとの catch 漏れに依存しない。QA-002: TOTP 未登録のまま他の API を叩くと
  // サーバが 403 TOTP_SETUP_REQUIRED を返す。ここでも素早く気づけるようにしておく。
  useEffect(() => onAuthEvent((ev) => {
    if (ev === "sessionExpired") {
      // 入力途中のフォームを、この端末に下書きとして残す（同じ利用者がログインし直して同じ画面に戻ったら復元）
      flushDrafts();
      // M2: clearMe は利用者ごとのキャッシュ（検索の候補・台帳の行・ロール名など）も消す
      clearMe();
      // H-1: 「ログインの有効期限が切れました」は、直前までログイン済みだった画面で
      // セッションが切れたときだけ出す。初回起動時の /auth/me プローブ（cur が既に null）では出さない。
      setMe((cur) => { if (cur) setSessionNotice(true); return null; });
    } else if (ev === "totpSetupRequired") {
      // サーバがこの 403 を返すのは LOCAL セッションだけ（server.ts）なので、ここも揃える
      setMe((cur) => (cur ? { ...cur, totpEnabled: false, hasLocalCredential: true, sessionAuthMethod: "LOCAL" } : cur));
    }
  }), []);

  // ⌘K / Ctrl+K で横断検索
  useEffect(() => {
    if (!me) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [me]);

  const go = useCallback((hash: string) => {
    if (location.hash === hash || (hash === "#/" && location.hash === "")) {
      startTransition(() => setRoute(parseRoute(hash)));
      return;
    }
    location.hash = hash;   // hashchange が画面を切り替える
  }, []);
  const shell = useMemo<Shell>(() => ({ go, openSearch: () => setSearchOpen(true) }), [go]);

  async function logout() {
    // H-02: ログアウトの POST 自体が失敗しても（既にセッションが切れている等）、
    // 画面は必ずログイン画面に戻す。例外で止まって「押しても何も起きない」にしない。
    try {
      await api.post("/auth/logout", {});
    } catch { /* 画面はログインに戻す */ } finally {
      clearAllDrafts();
      clearMe();
      viewpoint.reset();
      setSessionNotice(false);
      setMe(null);
      // M2: モジュールに残った読み込み結果（検索の候補・台帳の行など）を取りこぼしなく消すため、
      // ページごと読み込み直す。前の利用者が開いていた画面（URL のハッシュ）にも戻さない
      history.replaceState(null, "", location.pathname + location.search);
      location.reload();
    }
  }

  if (!checked) return <div className="login-wrap"><PageSkeleton /></div>;
  if (!me) {
    return (
      <>
        <Login
          notice={sessionNotice ? t("login.expired") : null}
          onSignedIn={(rn) => { setSessionNotice(false); setRecoveryNotice(rn ?? null); void refreshMe().catch(() => {}); refresh(); }}
        />
        <ToastRegion closeLabel={t("common.close")} />
      </>
    );
  }

  // QA-002: ローカル認証でまだ TOTP を登録していない利用者は、登録が済むまで
  // 他の画面を一切出さない（サーバ側も同じ方針を 403 TOTP_SETUP_REQUIRED で強制する）。
  // M-1: OIDC（Google/Entra）でログインしたセッションは sessionAuthMethod が "OIDC" なので対象外。
  if (me.sessionAuthMethod === "LOCAL" && !me.totpEnabled) {
    return (
      <ShellContext value={shell}>
        <div className="login-wrap">
          <main className="login" id="main">
            {recoveryNotice && <RecoveryNoticeBanner notice={recoveryNotice} onDismiss={() => setRecoveryNotice(null)} />}
            <Suspense fallback={<PageSkeleton />}>
              <TotpOnlySetup displayName={me.displayName} onDone={() => { refresh(); }} onLogout={() => void logout()} />
            </Suspense>
          </main>
        </div>
        <ToastRegion closeLabel={t("common.close")} />
      </ShellContext>
    );
  }

  return (
    <ShellContext value={shell}>
      <a className="skip-link" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>{t("nav.skip")}</a>
      <div className="app" data-past={past ? "" : undefined}>
        <Sidebar me={me} route={route} onLogout={() => void logout()} />
        <div className="main-col">
          <AsOfBand onPastChange={setPast} routeName={route.name} />
          <OfflineBanner />
          <UpdateBanner />
          <main className="content" id="main" tabIndex={-1}>
            {recoveryNotice && <RecoveryNoticeBanner notice={recoveryNotice} onDismiss={() => setRecoveryNotice(null)} />}
            <ErrorBoundary resetKey={routeKey(route)} fallback={({ chunk, retry }) => (
              <RouteError chunk={chunk} retry={retry} />
            )}>
              <Suspense fallback={<PageSkeleton />}>
                <ViewTransition key={routeKey(route)} enter="page-enter" exit="page-exit" default="none">
                  <div className="route">
                    <RouteView route={route} me={me} />
                  </div>
                </ViewTransition>
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      </div>
      <TabBar route={route} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen} />
      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} me={me} route={route} onLogout={() => void logout()} />
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} go={go} />
      <ToastRegion closeLabel={t("common.close")} />
    </ShellContext>
  );
}

function RouteView({ route, me }: { route: Route; me: Me }): ReactNode {
  switch (route.name) {
    case "home": return <Home />;
    case "notifications": return <NotificationsPage />;
    case "readiness": return <Dashboard />;
    case "obligations": return <Obligations />;
    case "incidents": return <Incidents />;
    case "report": return <ReportPage />;
    case "training": return <Training />;
    case "criteria": return <Criteria focusCode={route.code} />;
    case "settings": return <Settings focus={route.focus} />;
    case "roles": return <Roles />;
    case "governance": return <Governance key={route.tab ?? ""} initialTab={route.tab} />;
    case "coverage": return <Coverage />;
    case "list": return <RegisterList />;
    case "register": return <RegisterEntries code={route.code} focusKey={route.key} />;
    case "requests": return <RequestList />;
    case "requestNew": return <RequestNew />;
    case "request": return <RequestDetailView id={route.id} meId={me.userId} />;
    case "notFound": return <NotFound />;
  }
}

function NotFound() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("err.pageNotFound.title")} />
      <EmptyState title={t("err.pageNotFound.title")} body={t("err.pageNotFound.body")}
                  action={<a className="btn primary" href="#/">{t("err.pageNotFound.home")}</a>} />
    </>
  );
}

/** 画面単位のエラー境界の表示。ページ全体を白くせず、次の操作を出す。 */
function RouteError({ chunk, retry }: { chunk: boolean; retry: () => void }) {
  const { t } = useI18n();
  // オフライン中の読み込み失敗は「新しい版」ではなく通信の失敗として伝える
  const offline = chunk && navigator.onLine === false;
  return (
    <div style={{ paddingTop: "var(--s5)" }}>
      <div className="error-panel" role="alert">
        <p className="ep-title">{offline ? t("err.network.title") : chunk ? t("sys.newVersion") : t("err.load.title")}</p>
        <p className="ep-body">{offline ? t("err.network.bodyLoad") : chunk ? t("sys.newVersionBody") : t("err.boundary.body")}</p>
        <div className="ep-actions">
          <button type="button" className="primary" onClick={() => location.reload()}>{t("sys.reload")}</button>
          {!chunk && <button type="button" onClick={retry}>{t("err.action.reload")}</button>}
          <a className="btn" href="#/">{t("err.pageNotFound.home")}</a>
        </div>
      </div>
    </div>
  );
}
